/**
 * 비디오 목록 공통 Mixin
 * - 검색, 페이징, 로딩 상태 관리 로직 공통화
 * - 비디오 페이지에서 사용
 */

import { parseVideoSource } from '@/utils/videoSource';
import placeholder from '@/assets/images/video-placeholder.svg';

export default {
  data() {
    return {
      videos: [],
      loading: false,
      error: null,

      // 페이징 상태
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,

      // 검색 상태
      searchKeyword: '',

      // UI 상태
      hasMore: false
    };
  },
  async created() {
    await this.loadVideos();
  },
  methods: {
    /**
     * 비디오 목록 조회 (서버 페이지네이션)
     * - 자식 컴포넌트에서 fetchAPI 메서드를 반드시 구현해야 함
     * @param {boolean} isLoadMore - 더보기 여부 (true: 누적, false: 초기화)
     */
    async loadVideos(isLoadMore = false) {
      if (this.loading) return;

      // fetchAPI 메서드가 정의되어 있는지 확인
      if (typeof this.fetchAPI !== 'function') {
        console.error('fetchAPI method must be implemented in the component');
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        };

        // 검색어가 있으면 추가
        if (this.searchKeyword) {
          params.searchtext = this.searchKeyword;
        }

        const response = await this.fetchAPI(params);
        const apiVideos = response.data;

        // API 데이터를 VideoCard 형식으로 변환
        const transformedVideos = apiVideos.map(video => {
          const parsed = parseVideoSource(video.youtube_link);
          const thumbnailUrl = video.thumbnail || parsed.autoThumbnail || placeholder;
          return {
            id: video.id,
            title: video.title,
            subtitle: video.subtitle,
            publishDate: video.created_dttm,
            playType: parsed.playType,
            embedUrl: parsed.embedUrl,
            sourceUrl: video.youtube_link,
            thumbnailUrl,
            originalData: video // 원본 데이터도 보관
          };
        });

        // 더보기인 경우 기존 데이터에 추가, 아니면 교체
        if (isLoadMore) {
          this.videos = [...this.videos, ...transformedVideos];
        } else {
          this.videos = transformedVideos;
        }

        // 전체 개수는 응답 헤더에서 가져옴
        this.totalCount = parseInt(response.headers['x-total-count'] || 0);

        // 더 불러올 데이터가 있는지 확인
        this.hasMore = this.videos.length < this.totalCount;

      } catch (error) {
        this.error = '데이터를 불러오는 중 오류가 발생했습니다.';
        console.error('===== API 호출 에러 =====');
        console.error('에러:', error);
        console.error('에러 상세:', error.response?.data || error.message);
        console.error('========================');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 더보기 버튼 클릭 핸들러
     */
    async loadMoreVideos() {
      if (!this.hasMore || this.loading) return;

      this.currentPage += 1;
      await this.loadVideos(true);
    },

    /**
     * 검색 핸들러
     * @param {string} keyword - 검색어
     */
    async handleSearch(keyword) {
      // 검색어 저장
      this.searchKeyword = keyword;

      // 페이지 초기화
      this.currentPage = 1;
      this.videos = [];

      // 검색 조건으로 재조회
      await this.loadVideos(false);
    }
  }
};